import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  SimpleChange,
  SimpleChanges,
} from '@angular/core';

import { SessionService } from 'src/app/auth/session.service';
import AppSettings from 'src/app/common/app.settings';
import AppValues from 'src/app/common/app.values';
import {
  AddCommentContentWithPositionParent,
  CommentPosition,
  CommentView,
  FullRootCommentView,
  LoadedCommentsState,
  SendMessageData,
} from 'src/app/interfaces/posts.interface';
import { DialogMessagePlaceholders } from 'src/app/notifications/dialogs/dialog-message/dialog-message-placeholders.model';
import { PostPermissions } from 'src/app/swagger-api';
import { CommentFormStatus } from './comments-form.status';
import { COMMENT_PLACEHOLDER_PHRASES } from './comments.values';

@Component({
  selector: 'app-comments',
  templateUrl: './comments.component.html',
  styleUrls: ['./comments.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CommentsComponent implements OnChanges {
  @Input({ required: true }) public loadedComments: LoadedCommentsState;
  @Input({ required: true }) public postPermissions: PostPermissions;

  public readonly MAX_LENGTH: number = AppValues.COMMENT_MAX_LENGTH;
  public readonly PLACEHOLDER_PHRASES: DialogMessagePlaceholders = COMMENT_PLACEHOLDER_PHRASES;

  public AppSettings: typeof AppSettings = AppSettings;

  @Output() private dirtyForm: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() private removeComment: EventEmitter<CommentPosition> = new EventEmitter<CommentPosition>();
  @Output() private loadMoreComments: EventEmitter<CommentPosition> = new EventEmitter<CommentPosition>();
  @Output() private loadAllComments: EventEmitter<CommentPosition> = new EventEmitter<CommentPosition>();
  @Output() private addComment: EventEmitter<AddCommentContentWithPositionParent> =
    new EventEmitter<AddCommentContentWithPositionParent>();

  private commentFormStatus: CommentFormStatus;

  private sessionService = inject(SessionService);

  public ngOnChanges(changes: SimpleChanges): void {
    const loadedCommentsChanges: SimpleChange = changes['loadedComments'];
    if (loadedCommentsChanges) {
      this.commentFormStatus = new CommentFormStatus(this.loadedComments);
    }
  }

  public needsRootPagination(): boolean {
    return this.loadedComments.totalRootCommentsCount > this.loadedComments.rootCommentFullViews.length;
  }

  public needsReplyPagination(indexRoot: number): boolean {
    const rootComment: FullRootCommentView = this.loadedComments.rootCommentFullViews[indexRoot];
    return rootComment.totalReplyCommentsCount > rootComment.replyCommentViews.length;
  }

  public onRemoveComment(commentPosition: CommentPosition): void {
    this.removeComment.emit(commentPosition);
  }

  public onLoadMoreComments(parentCommentPosition: CommentPosition | null): void {
    this.loadMoreComments.emit(parentCommentPosition);
  }

  public onLoadAllComments(parentCommentPosition: CommentPosition | null): void {
    this.loadAllComments.emit(parentCommentPosition);
  }

  public sendMessage(sendMessageData: SendMessageData, parentCommentPosition?: CommentPosition | null): void {
    this.addComment.emit({
      sendMessageData: {
        text: sendMessageData.message,
        repliedCommentPosition: sendMessageData.position,
      },
      parentCommentPosition,
    });
  }

  public isCommentCreator(commentView: CommentView): boolean {
    return this.sessionService.userId === commentView.source.creator.user_id;
  }

  public get isAdminSession(): boolean {
    return this.sessionService.isAdminSession();
  }

  public setFormStatus(isFormChanged: boolean): void {
    this.commentFormStatus.updateMainFormStatus(isFormChanged);
    this.dirtyForm.emit(this.commentFormStatus.isFormChanged());
  }

  public setReplyFormStatus(isFormChanged: boolean, commentPosition: CommentPosition): void {
    this.commentFormStatus.updateCommentFormStatus(isFormChanged, commentPosition);
    this.dirtyForm.emit(this.commentFormStatus.isFormChanged());
  }
}
